import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { GET_AEPS_HISTORY_LIST, GET_AEPS_WITHDRAWAL_INQUIRY, FING_PAY_CASH_WITHDRAWAL_CHECK_STATUS_API } from './../../Utils/appConstants';
import { getRequest, postRequest } from './../../Utils/AppApi';
import { useHistory } from "react-router-dom";
import { DatePicker, Tooltip, Select, Space, Tag } from 'antd';
import { getUTCToLocalDateTimeFormat, queryDate, toDayDate } from '../../Utils/function';
import { aepsServiceInArray, aepsStatus } from '../../Utils/Common';
import { toast } from 'react-toastify';
import PropagateLoader from "react-spinners/PropagateLoader";
import { PrinterOutlined } from "@ant-design/icons";
import moment from 'moment';   // 👈 added

/* ============================================================
   PERSISTENCE HELPERS
============================================================ */
const FILTERS_STORAGE_KEY = 'aepsHistoryFilters';
const RESTORE_FLAG_KEY = 'aepsHistoryRestore';

const consumeRestoreFlag = () => {
    try {
        const flag = sessionStorage.getItem(RESTORE_FLAG_KEY);
        if (flag === '1') {
            sessionStorage.removeItem(RESTORE_FLAG_KEY);
            return true;
        }
    } catch { /* ignore */ }
    return false;
};

const loadSavedFilters = () => {
    if (!consumeRestoreFlag()) {
        try { sessionStorage.removeItem(FILTERS_STORAGE_KEY); } catch { /* ignore */ }
        return {};
    }
    try {
        const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const restoreDate = (str) => {
    if (!str) return toDayDate();
    const m = moment(str, 'DD-MM-YYYY');
    return m.isValid() ? m : toDayDate();
};
/* ============================================================ */

function AepsHistory(props) {

    const history = useHistory();
    const limit = 10;

    const dateFormat = 'DD-MM-YYYY';

    const [saved] = useState(() => loadSavedFilters());   // 👈 consume flag once

    const [searchTransId, setSearchTransId] = useState(saved.searchTransId ?? null);
    const [searchMobile, setSearchMobile] = useState(saved.searchMobile ?? null);
    const [searchServiceName, setSearchServiceName] = useState(saved.searchServiceName ?? null);
    const [searchAadhar, setSearchAadhar] = useState(saved.searchAadhar ?? null);
    const [endDate, setEndDate] = useState(restoreDate(saved.endDate));
    const [startDate, setStartDate] = useState(restoreDate(saved.startDate));
    const [getData, setData] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(saved.page || 1);
    const [isLimit, setLimit] = useState(saved.isLimit || limit);

    // ----------------------
    // 1. EXISTING transactionInquiry (used by CLAIM button)
    // ----------------------
    const transactionInquiry = (data) => {
        setLoading(true);
        const payload = {
            transactionId: data.transactionId,
        };
        let URL = GET_AEPS_WITHDRAWAL_INQUIRY;

        if (data.apiServiceName === "FINGPAY") {
            URL = FING_PAY_CASH_WITHDRAWAL_CHECK_STATUS_API;
        }

        postRequest(`${URL}`, payload, history)
            .then((response) => {
                if (response.success === true) {
                    toast.success(response.message);
                    getDataReports();
                } else {
                    toast.error(response.message);
                }
                setLoading(false);
            })
            .catch(function (error) {
                console.log(error);
                setLoading(false);
            });
    };

    // ----------------------
    // 2. handlePrintInvoice – set restore flag before navigating
    // ----------------------
    const handlePrintInvoice = async (record) => {
        // 👇 mark "coming back" before any navigation
        try { sessionStorage.setItem(RESTORE_FLAG_KEY, '1'); } catch { /* ignore */ }

        // If status is 0 (pending), call inquiry first
        if (record.status === 0) {
            setLoading(true);
            try {
                const payload = { transactionId: record.transactionId };
                const URL = record.apiServiceName === "FINGPAY"
                    ? FING_PAY_CASH_WITHDRAWAL_CHECK_STATUS_API
                    : GET_AEPS_WITHDRAWAL_INQUIRY;

                const response = await postRequest(URL, payload, history);
                if (response.success === true) {
                    toast.success(response.message);
                    history.push(`/aepsinvoice/${record.transactionId}`);
                } else {
                    toast.error(response.message);
                }
            } catch (error) {
                console.log(error);
                toast.error("Failed to process inquiry");
            } finally {
                setLoading(false);
            }
        } else {
            history.push(`/aepsinvoice/${record.transactionId}`);
        }
    };

    // ----------------------
    // 3. Columns definition
    // ----------------------
    const columns = [
        {
            name: 'Date Time',
            cell: row => getUTCToLocalDateTimeFormat(row.createdAt),
            width: "200px",
            headerStyle: (selector, id) => {
                return { textAlign: "center" };
            },
        },
        {
            name: 'Trans ID',
            selector: row => <Tooltip placement="bottom" title={row.transactionId && row.transactionId}>{row.transactionId && row.transactionId}</Tooltip>,
            width: "180px",
            headerStyle: (selector, id) => {
                return { textAlign: "center" };
            },
        },
        {
            name: 'Service Name',
            selector: row => <Tooltip placement="bottom" title={row.mode}>{row.mode}</Tooltip>,
            width: "180px",
            headerStyle: (selector, id) => {
                return { textAlign: "center", wordWrap: "break-word" };
            },
        },
        {
            name: 'Bank',
            selector: row => (!row.bankName) ? "-" : row.bankName,
            width: "130px",
            headerStyle: (selector, id) => {
                return { textAlign: "center" };
            },
        },
        {
            name: 'Aadhaar',
            selector: row => (!row.aadhaar) ? "-" : "XXXXXXXX" + row.aadhaar,
            width: "130px",
            headerStyle: (selector, id) => {
                return { textAlign: "center" };
            },
        },
        {
            name: 'Mobile',
            selector: row => row.mobile,
            width: "140px",
            headerStyle: (selector, id) => {
                return { textAlign: "center" };
            },
        },
        {
            name: 'Amount',
            selector: row => row.amount ? row.amount : 0,
            width: "100px",
            headerStyle: (selector, id) => {
                return { textAlign: "center" };
            },
        },
        {
            name: 'Bank RRN',
            selector: row => row.bankRRN,
            width: "130px",
            headerStyle: (selector, id) => {
                return { textAlign: "center" };
            },
        },
        {
            name: 'Status',
            selector: row => aepsStatus(row.status),
            width: "150px",
            headerStyle: (selector, id) => {
                return { textAlign: "center" };
            },
        },
        {
            name: 'Action',
            selector: (record, key) => (
                <Space key={key} size="middle">
                    {/* CLAIM button */}
                    {(record.status === 0) ? (
                        <Tag
                            color="#5F249F"
                            onClick={() => transactionInquiry(record)}
                            className="pointer"
                        >
                            CLAIM
                        </Tag>
                    ) : "-"}

                    {/* PRINT INVOICE button */}
                    <Tooltip title="Print Invoice">
                        <PrinterOutlined
                            className="pointer"
                            style={{ fontSize: "16px", color: "#1890ff" }}
                            onClick={() => handlePrintInvoice(record)}
                        />
                    </Tooltip>
                </Space>
            )
        },
        {
            name: 'Message',
            selector: row => <Tooltip placement="bottom" title={row.message && row.message}>{row.message && row.message}</Tooltip>,
            width: "200px",
            headerStyle: (selector, id) => {
                return { textAlign: "center" };
            },
        }
    ];

    // ----------------------
    // 4. Helper functions
    // ----------------------
    const objectToQueryString = (obj) => {
        var str = [];
        for (var p in obj)
            if (obj.hasOwnProperty(p)) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        return str.join("&");
    };

    const params = { page: page, limit: isLimit };

    const getDataReports = (data) => {
        if (data) {
            setLimit(data);
            params.limit = data;
        }
        if (startDate && endDate) {
            params.startDate = startDate ? queryDate(startDate) : "";
            params.endDate = endDate ? queryDate(endDate) : "";
        }

        params.searchTransId = searchTransId ? searchTransId : "";
        params.searchMobile = searchMobile ? searchMobile : "";
        params.searchServiceName = "Cash Withdrawal";
        params.searchAadhar = searchAadhar ? searchAadhar : "";

        const queryString = objectToQueryString(params);
        getRequest(`${GET_AEPS_HISTORY_LIST}?${queryString}`, history)
            .then((response) => {
                if (response.success === true) {
                    setData(response.data);
                    setTotalRecord(response.data.totalDocs);
                    setPage(response.data.page);
                } else {
                    toast.error(response.message);
                    setData([]);
                    setTotalRecord(0);
                    setPage(1);
                }
            })
            .catch(function (error) {
                console.log(error);
            });
    };

    const handlePageChange = (page, size) => {
        setPage(page);
    };

    const onChangeStartDate = (date, dateString) => {
        setStartDate(date);
    };

    const onChangeEndDate = (date, dateString) => {
        setEndDate(date);
    };

    const handleSearch = () => {
        getDataReports();
    };

    /* ---------------------------------------------------------
       Reset — also wipe saved filters + restore flag
    --------------------------------------------------------- */
    const handleClear = () => {
        try {
            sessionStorage.removeItem(FILTERS_STORAGE_KEY);   // 👈
            sessionStorage.removeItem(RESTORE_FLAG_KEY);      // 👈
        } catch { /* ignore */ }

        setSearchAadhar('');
        setSearchTransId('');
        setSearchMobile('');
        setSearchServiceName(null);
        setStartDate('');
        setEndDate('');
        setPage(1);
    };

    /* ============================================================
       PERSIST FILTERS ON EVERY CHANGE
    ============================================================ */
    useEffect(() => {
        const toSave = {
            startDate: startDate ? moment(startDate).format(dateFormat) : null,
            endDate: endDate ? moment(endDate).format(dateFormat) : null,
            searchTransId,
            searchMobile,
            searchServiceName,
            searchAadhar,
            page,
            isLimit,
        };
        try {
            sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toSave));
        } catch { /* ignore */ }
    }, [
        startDate, endDate,
        searchTransId, searchMobile, searchServiceName, searchAadhar,
        page, isLimit,
    ]);
    /* ============================================================ */

    // ----------------------
    // 5. Lifecycle
    // ----------------------
    useEffect(() => {
        getDataReports();
    }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    // ----------------------
    // 6. Render
    // ----------------------
    if (isLoading) {
        return (
            <div className="content text-center" style={{ margin: "250px 0" }}>
                <PropagateLoader color="#5851D2" loading={isLoading} size={25} />
            </div>
        );
    }

    return (
        <div>
            <div className="row">
                <div className='col-md-12 date-search mb-0'>
                    <div className='row'>
                        <div className='col-md-2 col-lg-2'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-blue'>From</label>
                                <DatePicker className="form-control form-control-border" value={startDate} format={dateFormat} onChange={onChangeStartDate} />
                            </div>
                        </div>
                        <div className='col-md-2 col-lg-2'>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-red'>To</label>
                                <DatePicker className="form-control form-control-border" value={endDate} format={dateFormat} onChange={onChangeEndDate} />
                            </div>
                        </div>
                        <div className='col-md-3 col-lg-3 d-flex'>
                            <div className="form-group mr-3">
                                <label className='date-search-label mb-0 text-red'></label>
                                <input type="text" className="form-control form-control-border" placeholder="Aadhar Number" onChange={(e) => setSearchAadhar(e.target.value)} value={searchAadhar || ''} />
                            </div>
                            <div className="form-group">
                                <label className='date-search-label mb-0 text-red'></label>
                                <input type="text" className="form-control form-control-border" placeholder="Trans ID" onChange={(e) => setSearchTransId(e.target.value)} value={searchTransId || ''} />
                            </div>
                        </div>
                        <div className='col-md-3 col-lg-3 d-flex'>
                            <div className="form-group mr-3">
                                <label className='date-search-label mb-0 text-red'></label>
                                <input type="text" className="form-control form-control-border" placeholder="Mobile Number" onChange={(e) => setSearchMobile(e.target.value)} value={searchMobile || ''} />
                            </div>
                        </div>
                        <div className='col-md-4 col-lg-1'>
                            <div><label className='date-search-label mb-0 text-red'>&nbsp;</label></div>
                            <button type="submit" className="btn btn-primary rounded-pill pl-4 pr-4" onClick={() => handleSearch()}>Search</button>
                        </div>
                        <div className='col-md-4 col-lg-1'>
                            <div><label className='date-search-label mb-0 text-red'>&nbsp;</label></div>
                            <button type="button" className="btn btn-default rounded-pill ml-3 pl-4 pr-4 pull-right" onClick={() => handleClear()}>Reset</button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="container-fluid pt-2 pb-5">
                <DataTable
                    title={""}
                    columns={columns}
                    data={getData.docs}
                    highlightOnHover
                    pagination
                    paginationServer
                    paginationTotalRows={getTotalRecord}
                    paginationDefaultPage={page}          
                    paginationPerPage={isLimit}
                    onChangeRowsPerPage={getDataReports}
                    onChangePage={handlePageChange}
                />
            </div>
        </div>
    );
}

export default AepsHistory;